// AvatarMenu.tsx

import React, { useState, MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, Divider, Box, Typography } from '@mui/material';
import { styled } from 'styled-components';
import CustomAvatar from '../CustomAvatar/CustomAvatar';
import { useAppSelector, useAppDispatch } from '@src/store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { truncateField, generateInitialsHelper, getBaseRoute } from '@src/helpers/Helpers';
import { clearUserSession } from '@src/helpers/ApiHelper';
import { SettingsIcon, LogoutIcon, SettingsIcon3 } from '../Icons';
import { ApplicationContext } from '@src/contexts/ApplicationContext';

const StyledMenuItem = styled(MenuItem)`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	align-content: center;
	height: 50%;
	color: #5c5c5c;
	letter-spacing: 0.2px;
`;

const StyledName = styled(Box)`
	display: flex;
	justify-content: start;
	align-items: center;
	height: 50%;
	padding: 4px 16px;
	color: var(--lighter-text-color);
	font-size: 14px;
	font-weight: 600;
	line-height: 19.1px;
	letter-spacing: 0.2px;
`;

interface Props {
	settingsIcon?: boolean;
}

const AvatarMenu: React.FC<Props> = ({ settingsIcon }) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { id } = useParams();

	const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);

	const logOut = () => {
		clearUserSession(undefined, undefined, navigate, () => apiService.logoutCallV2());
		hideMenu();
	};

	const hideMenu = () => {
		setAnchorEl(null);
	};

	const displayMenu = (event: MouseEvent) => {
		setAnchorEl(event.currentTarget);
	};

	const customBackdropStyle: React.CSSProperties = {
		backgroundColor: 'transparent',
	};

	return (
		<>
			{settingsIcon ? (
				<IconButton onClick={displayMenu}>
					<SettingsIcon3 />
				</IconButton>
			) : (
				<CustomAvatar
					src={currentUser?.profile}
					style={{ objectFit: 'contain' }}
					className="cursorPointer"
					generateBgColorBy={currentUser?.username}
					onClick={displayMenu}
				>
					{currentUser ? generateInitialsHelper(currentUser?.firstName) : ''}
				</CustomAvatar>
			)}
			<Menu
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				keepMounted
				PaperProps={{
					style: {
						height: 'auto',
						boxShadow: '0px 10px 25px 0px #0000001A',
					},
				}}
				MenuListProps={{
					style: {
						height: '100%',
					},
				}}
				BackdropProps={{
					style: customBackdropStyle,
				}}
				open={Boolean(anchorEl)}
				onClose={hideMenu}
				elevation={3}
			>
				<div style={{ width: '220px', background: 'transparent', height: '100%' }}>
					<StyledName>
						<Typography>{truncateField(currentUser?.firstName, 32)}</Typography>
					</StyledName>
					<Divider />
					<StyledMenuItem
						onClick={() => navigate(`/${getBaseRoute(currentUser?.type)}/${id}/settings/general`)}
						data-test-id="settings-button"
					>
						<SettingsIcon style={{ marginRight: '6px' }} /> Settings
					</StyledMenuItem>
					<StyledMenuItem onClick={logOut} data-test-id="logout-button">
						<LogoutIcon style={{ marginRight: '6px' }} /> Logout
					</StyledMenuItem>
				</div>
			</Menu>
		</>
	);
};

export default AvatarMenu;
